import * as go from 'gojs';
import type {
  UnitInfoTextInterface,
  UnitTextArrayInterface,
} from 'gojs/nodeTexts';
import type { InfoPanelInterface } from 'gojs/panelData';
import partition from 'util/partition';
import { getFont } from '../utils/font';
import { PanelNames } from '../utils/panelNames';
import { getTextStroke, getTitleStroke } from './baseUnitUtils';
import { RISK_DISPLAY } from 'util/riskAssessment';
import { isDebugMode } from 'hooks/useDebugMode';

const $ = go.GraphObject.make;

const getTexts = (node: InfoPanelInterface) => {
  const unitTextArray = node.unitTextArray as UnitTextArrayInterface[];
  const allTexts = unitTextArray.flatMap((u) => u.texts);
  const [titles, texts] = partition(allTexts, (t) => !!t.title);
  return { titles, texts, allTexts };
};

/**
 * Template for displaying unit info data
 */
export const InfoPanel = $(
  go.Panel,
  'Vertical',
  { defaultAlignment: go.Spot.Left },
  $(
    go.TextBlock,
    {
      name: PanelNames.INFO_PANEL_TITLE,
      margin: new go.Margin(0, 0, 6, 0),
      textAlign: 'left',
      spacingBelow: 2,
    },
    new go.Binding('visible', '', (node: InfoPanelInterface) => {
      const { titles } = getTexts(node);
      return titles.length > 0;
    }),
    new go.Binding('text', '', (node: InfoPanelInterface) => {
      const titles = getTexts(node).titles.map((t) => t.text);
      if (titles.length > 0) {
        return titles.join('\n');
      }
    }),
    getFont({ weight: 700 }),
    getTitleStroke(),
  ),
  $(
    go.TextBlock,
    new go.Binding('visible', '', (node: InfoPanelInterface) => {
      const { texts } = getTexts(node);
      return texts.length > 0;
    }),
    new go.Binding('text', '', (node: InfoPanelInterface) => {
      const { texts } = getTexts(node);
      const infoTexts = texts.map((title: UnitInfoTextInterface) => title.text);
      if (infoTexts.length > 0) {
        return infoTexts.join('\n');
      }
    }),
    {
      margin: new go.Margin(0, 0, 0, 0),
      textAlign: 'left',
      spacingBelow: 2,
      spacingAbove: 2,
      column: 0,
      stroke: getTextStroke(),
    },
    getFont({ weight: 500 }),
  ),
  $(
    go.Panel,
    'Auto',
    { margin: new go.Margin(8, 0, 0, 0) },
    new go.Binding('visible', '', (node: InfoPanelInterface) => {
      return (
        !!node.riskAssessmentLevel &&
        node.showRiskLevel &&
        node.displayRiskLevel !== false &&
        node.isApprover !== false
      );
    }),
    $(
      go.Shape,
      'RoundedRectangle',
      {
        parameter1: 8,
        strokeWidth: 1,
      },
      new go.Binding('fill', '', (node: InfoPanelInterface) => {
        const riskLevel = node.riskAssessmentLevel;
        if (!riskLevel) return 'rgba(0,0,0,0)';
        return RISK_DISPLAY[riskLevel].bgColor;
      }),
      new go.Binding('stroke', '', (node: InfoPanelInterface) => {
        const riskLevel = node.riskAssessmentLevel;
        if (!riskLevel) return 'rgba(0,0,0,0)';
        return RISK_DISPLAY[riskLevel].bgColor;
      }),
    ),
    $(
      go.TextBlock,
      {
        stroke: 'black',
        alignment: go.Spot.Center,
        margin: new go.Margin(0, 4, 0, 4),
        isMultiline: false,
      },
      getFont({ weight: 500 }),
      new go.Binding('text', '', (node: InfoPanelInterface) => {
        const riskLevel = node.riskAssessmentLevel;
        if (!riskLevel) return '';
        const label = RISK_DISPLAY[riskLevel].label;
        const percentage =
          isDebugMode() &&
          node.riskPercentage !== null &&
          node.riskPercentage !== undefined
            ? ` (${node.riskPercentage.toFixed(1)}%)`
            : '';
        return `${label}${percentage}`;
      }),
    ),
  ),
);
